import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {isMobileAtom} from 'atoms/layout';
import {Layout} from 'components/DSYS/Layout';
import {DSYS_RAIL_INSET_LEFT_PX, DSYS_RAIL_INSET_RIGHT_PX} from 'components/DSYS/layoutConstants';
import {SkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {ReactNode, useEffect, useRef} from 'react';

import {TeamsPage} from './TeamsPage';
import {TeamsSection, TeamsSectionTabs} from './TeamsSectionTabs';

type TeamOwnershipDirectoryLayoutProps = {
  activeSection: TeamsSection;
  breadcrumb: ReactNode;
  children: ReactNode;
  detailLoadingVariant?: 'detail' | 'directory';
  // The list, given the rail's bleed (full-bleed rows) or none (narrow screens).
  directory: (bleed?: {left: number; right: number}) => ReactNode;
  isDetailError?: boolean;
  isDirectoryEmpty?: boolean;
  isDirectoryError?: boolean;
  isDirectoryLoading?: boolean;
  onSearchChange: (search: string) => void;
  search: string;
  searchPlaceholder: string;
  searchResetKey?: unknown;
};

// The search row bleeds back across the page rail's insets, like full-bleed rows.
const RAIL_BLEED = {left: DSYS_RAIL_INSET_LEFT_PX, right: DSYS_RAIL_INSET_RIGHT_PX};

export const TeamOwnershipDirectoryLayout = ({
  activeSection,
  breadcrumb,
  children,
  detailLoadingVariant,
  directory,
  isDetailError = false,
  isDirectoryEmpty = false,
  isDirectoryError = false,
  isDirectoryLoading = false,
  onSearchChange,
  search,
  searchPlaceholder,
  searchResetKey = search,
}: TeamOwnershipDirectoryLayoutProps) => {
  const directoryContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useAtomValue(isMobileAtom);

  // A new search starts the results at the top: the directory's own scroll on
  // narrow screens, the page rail's on wider ones.
  useEffect(() => {
    const container = directoryContainerRef.current;
    if (!container) return;
    container.scrollTop = 0;
    const rail = container.closest('[data-page-rail]');
    if (rail) rail.scrollTop = 0;
  }, [searchResetKey]);

  const searchInput = (bleed?: {left: number; right: number}) => (
    <Layout.RailSearchRow
      value={search}
      onChange={onSearchChange}
      placeholder={searchPlaceholder}
      bleed={bleed}
    />
  );
  // States sit one row inset in; the list's rows carry their own inset.
  const directoryContent = (bleed?: {left: number; right: number}) => (
    <SkeletonFadeIn
      loading={isDirectoryLoading}
      skeleton={
        <Stack gap="12" padding="8">
          <Skeleton.Text width={220} />
          <Skeleton.Text width={180} />
          <Skeleton.Text width={240} />
        </Stack>
      }
    >
      {isDirectoryError ? (
        <Box paddingX="8">
          <Text color="subtle">{t('ownership_error')}</Text>
        </Box>
      ) : isDirectoryEmpty ? (
        <Box paddingX="8">
          <Text color="subtle">{t('ownership_empty')}</Text>
        </Box>
      ) : (
        directory(bleed)
      )}
    </SkeletonFadeIn>
  );
  const detail = (
    <SkeletonFadeIn
      loading={Boolean(detailLoadingVariant)}
      skeleton={
        <Stack gap="8">
          <Skeleton.Text width={120} />
          <Skeleton.Text width={320} />
          {detailLoadingVariant === 'detail' ? <Skeleton.Text width={280} /> : null}
        </Stack>
      }
    >
      {isDetailError ? <Text color="subtle">{t('ownership_error')}</Text> : children}
    </SkeletonFadeIn>
  );

  if (!isMobile) {
    return (
      <TeamsPage
        active={activeSection}
        rail={
          // Search and the directory's rows are full-bleed rail rows, like Teams'.
          // Search sits flush under the header line, like the first row of a rail.
          <Box>
            {searchInput(RAIL_BLEED)}
            {/* The rail scrolls the directory with it. */}
            <Box ref={directoryContainerRef} paddingTop="4" style={{paddingBottom: 30}}>
              {directoryContent(RAIL_BLEED)}
            </Box>
          </Box>
        }
      >
        {/* No breadcrumb: the header, switch and selected rail row say where you are. */}
        {detail}
      </TeamsPage>
    );
  }

  return (
    <Layout.Container>
      <Layout.Nav>
        <Stack gap="12">
          <TeamsSectionTabs active={activeSection} />
          {searchInput()}
          <Box
            ref={directoryContainerRef}
            style={{
              maxHeight: 'calc(100vh - 190px)',
              overflowX: 'hidden',
              overflowY: 'auto',
              paddingBottom: 30,
              paddingRight: 12,
            }}
          >
            {directoryContent()}
          </Box>
        </Stack>
      </Layout.Nav>
      <Layout.Body breadcrumb={breadcrumb}>{detail}</Layout.Body>
    </Layout.Container>
  );
};
